import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import PortableWifiOffIcon from "@mui/icons-material/PortableWifiOff";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import ListItemButton from "@mui/material/ListItemButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { groupSymbolDataUrl } from "../models/groupSymbolSvg";
import { MapAssetRow } from "../models/mapAssetRow";
import { DRIVE_TYPE_ICONS } from "./driveTypeIcon";
import { DeviceActivityIcon } from "@/components/DeviceActivityIcon";
import { useFormattedDuration } from "@/hooks/metrics/useFormattedDuration";
import { DeviceActivity } from "@/utilities/deviceActivity";


/** One level bar under a vehicle, when the vehicle reports that level. */
function LevelBar({
  label,
  percent,
  color,
}: {
  label: string;
  percent: number;
  color: "success" | "warning" | "primary";
}) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {`${Math.round(percent)} %`}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={color}
        sx={{ height: 4, borderRadius: 2 }}
      />
    </Box>
  );
}

interface Props {
  row: MapAssetRow;
  selected: boolean;
  /** The group's own symbol, when it has one, so the row shows what the map shows. */
  symbolSvg?: string;
  onSelect: (deviceId: string) => void;
}

/**
 * One vehicle in the list beside the map.
 *
 * The row leads with the group's own symbol where there is one, then the arrow-while-moving and
 * square-while-standing the map markers use, then how it is powered, then its name. The bars
 * underneath carry the levels, so the drive-type icon carries no number of its own.
 */
export function VehicleListRow({ row, selected, symbolSvg, onSelect }: Props) {
  const { t } = useTranslation();
  const duration = useFormattedDuration(row.durationSeconds);
  const DriveTypeIcon = DRIVE_TYPE_ICONS[row.driveType];
  const driveTypeLabel = t(`map.vehicleList.driveType.${row.driveType}`);
  const chargingLabel = t("map.vehicleList.charging");
  const noSignalLabel = t("map.noSignal");

  // Not `deviceActivity(status)`: the driving state shown here is the map's, which is held back
  // for a moment against flicker, and the list has to agree with the marker beside it.
  const activity: DeviceActivity = row.isOffline
    ? "offline"
    : row.isDriving
      ? "driving"
      : "stopped";

  return (
    <ListItemButton
      selected={selected}
      onClick={() => onSelect(row.deviceId)}
      sx={{ display: "block", py: 0.75 }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center">
        {symbolSvg ? (
          <Box
            component="img"
            src={groupSymbolDataUrl(symbolSvg)}
            alt=""
            sx={{ width: 20, height: 20, opacity: row.isOffline ? 0.4 : 1 }}
          />
        ) : null}

        {/*
          Always shown, even beside a group's own symbol. The symbol says which group a vehicle
          belongs to and nothing about what it is doing, so without this a fleet drawn with
          symbols had no way of telling a moving vehicle from a standing one at a glance.
        */}
        <DeviceActivityIcon activity={activity} />

        <DriveTypeIcon fontSize="small" color="action" aria-label={driveTypeLabel} />

        {row.isCharging ? (
          <Tooltip title={chargingLabel}>
            <BatteryChargingFullIcon fontSize="small" color="primary" aria-label={chargingLabel} />
          </Tooltip>
        ) : null}

        {row.isOffline ? (
          <Tooltip title={noSignalLabel}>
            <PortableWifiOffIcon fontSize="small" color="error" aria-label={noSignalLabel} />
          </Tooltip>
        ) : null}

        <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 600 }}>
          {row.name}
        </Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary" component="div">
        {`${row.isDriving ? t("map.driving") : t("map.stopped")}${
          row.durationSeconds === undefined ? "" : ` · ${duration}`
        }`}
      </Typography>

      {row.zoneName ? (
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {row.zoneName}
        </Typography>
      ) : null}

      {row.address ? (
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {row.address}
        </Typography>
      ) : null}

      {/*
        Hidden only while the vehicle is out of contact: the reading is whatever it last sent, so
        one that lost signal at 72 km/h would otherwise read "standing, three days" with "72 km/h"
        underneath. A standing vehicle that is in contact reports zero, which is worth showing.
      */}
      {!row.isOffline && row.speed !== undefined ? (
        <Typography variant="caption" color="text.secondary" component="div">
          {`${t("map.speed")}: ${Math.round(row.speed)} km/h`}
        </Typography>
      ) : null}

      {row.soc !== undefined || row.fuelLevel !== undefined || row.adBlueLevel !== undefined ? (
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {row.soc !== undefined ? (
            <LevelBar label={t("map.vehicleList.battery")} percent={row.soc} color="success" />
          ) : null}
          {row.fuelLevel !== undefined ? (
            <LevelBar
              // A hydrogen tank arrives through the same diagnostic as a diesel tank, so only the
              // wording tells the reader which one they are looking at.
              label={
                row.driveType === "hydrogen"
                  ? t("map.vehicleList.driveType.hydrogen")
                  : t("map.vehicleList.fuel")
              }
              percent={row.fuelLevel}
              color="warning"
            />
          ) : null}
          {row.adBlueLevel !== undefined ? (
            <LevelBar
              label={t("map.vehicleList.adBlue")}
              percent={row.adBlueLevel}
              color="primary"
            />
          ) : null}
        </Stack>
      ) : null}
    </ListItemButton>
  );
}
